'use strict';

import {createHtmlElement, userPrevent} from "../../../functions";
import {isMobile, settingsData} from "../../../settings";
import gsap from "gsap";
import {CardTypes} from "../../../common";
import {changeLangToCurrent} from "../langSwitcher/langSwitcher";

export class TraverseSwitcher {
  #ANIMATION_DURATION = 0.5;

  #buttonsWrapper = null;
  #buttonsWindow = null;
  #plansBoxPreview = null;

  constructor(parent = document.querySelector('.options-card__plans-box')) {
    this.element = document.createElement('div');
    this.element.classList.add('traverse-switcher', 'background-backdrop-filter', 'hidden');
    this.element.addEventListener('click', this.controlButtonsHandler.bind(this));
    this.#buttonsWrapper = createHtmlElement(this.element, '', 'div', ['traverse-switcher__buttons-wrapper']);
    this.#buttonsWindow = createHtmlElement(this.#buttonsWrapper, '', 'div', ['traverse-switcher__window']);
    this.floorButtons = createHtmlElement(this.#buttonsWindow, '', 'div', ['traverse-switcher__buttons']);
    if (isMobile)
      this.#addText('floor');

    parent.appendChild(this.element);

    this.addFloorButtons();
    this.addControlButtons();
    this.setCurrentFloorButtonInFocus();
    this.checkControlButtonsVisibility();

    if (this.floorButtons.children.length <= 1)
      this.hide();
  }

  set listener(listener) {
    this.element.addEventListener('click', listener);
  }

  show() {
    if (this.floorButtons.children.length <= 1 || config.features?.vantage?.hideSwitcher)
      return;

    this.element.classList.remove('hidden');
  }

  hide() {
    this.element.classList.add('hidden');
  }

  setCurrentFloorButtonInFocus() {
    for (let i = 0; i < this.floorButtons.children.length; i++) {
      const currentRange = this.floorButtons.children[i].innerHTML.split('-');
      if (currentRange.length === 1 &&
        parseInt(currentRange[0]) === parseInt(settingsData.currentFloor) ||
        parseInt(currentRange[0]) <= parseInt(settingsData.currentFloor) &&
        parseInt(currentRange[1]) >= parseInt(settingsData.currentFloor)) {
        this.currentActiveButtonIndex = i;
        this.getElementsStyleParams();
        gsap.to(this.floorButtons, {top: (0 - this.buttonsWindowHeight) * i});
        break;
      }
    }
    this.checkControlButtonsVisibility();
  }

  #addText(text) {
    const textElement = createHtmlElement(this.element, changeLangToCurrent(text), 'span', ['traverse-switcher__text', 'language-string']);
    textElement.setAttribute('data-dictionary', text);
  }

  controlButtonsHandler(event) {
    if (userPrevent()) return;

    if (event.target.classList.contains('traverse-switcher__control-button-up')) {
      userPrevent(true);
      this.moveItemsUp();
    } else if (event.target.classList.contains('traverse-switcher__control-button-down')) {
      userPrevent(true);
      this.moveItemsDown();
    } else {
      return;
    }

    this.#plansBoxPreview = document.querySelector(`.${CardTypes.Options}-card__plans-box_preview`);
    this.#plansBoxPreview.removeAttribute('style');
    this.#plansBoxPreview.innerHTML = '';

    const vantage = createHtmlElement(
      this.#plansBoxPreview,
      '',
      'iframe',
      [
        `${CardTypes.Options}-card__plans-box_preview-content`,
        'plan-vantage'
      ]
    );
    vantage.setAttribute('src', this.floorButtons.children[this.currentActiveButtonIndex].getAttribute('vantage-url'));

    this.checkControlButtonsVisibility();
  }

  moveItemsUp() {
    this.getElementsStyleParams();

    this.currentActiveButtonIndex++;
    gsap.to(this.floorButtons,
      {
        top: (this.buttonsTop - this.buttonsWindowHeight),
        duration: this.#ANIMATION_DURATION,
        onComplete: () => { userPrevent(false); }}
    );
  }

  moveItemsDown() {
    this.getElementsStyleParams();

    this.currentActiveButtonIndex--;
    gsap.to(
      this.floorButtons,
      {
        top: (this.buttonsTop + this.buttonsWindowHeight),
        duration: this.#ANIMATION_DURATION,
        onComplete: () => { userPrevent(false); }}
    );
  }

  checkControlButtonsVisibility() {
    if (this.currentActiveButtonIndex === 0) {
      this.#hideElement(this.controlDown);
    }

    if (this.currentActiveButtonIndex === this.floorButtons.children.length - 1) {
      this.#hideElement(this.controlUp);
    }

    if (this.currentActiveButtonIndex > 0) {
      this.#showElement(this.controlDown);
    }

    if (this.currentActiveButtonIndex < this.floorButtons.children.length - 1) {
      this.#showElement(this.controlUp);
    }
  }

  #hideElement(element) {
    if (!element) return;
    element.disabled = true;
    element.style.opacity = 0;
  }

  #showElement(element) {
    if (!element) return;
    element.disabled = false;
    element.style = '';
  }

  getElementsStyleParams() {
    this.buttonsTop = parseFloat(getComputedStyle(this.floorButtons).top);
    this.buttonsHeight = parseFloat(getComputedStyle(this.floorButtons).height);
    this.buttonsWindowHeight = parseFloat(getComputedStyle(this.#buttonsWindow).height);
  }

  addFloorButtons() {
    const lastClickedFlatCrmData = settingsData.lastClickedFlat.userData.crm_data;
    const lastClickedFlatVantageYaw =
      parseInt(settingsData.lastClickedFlat.userData.crm_data.assets.vantage.split('yaw=')[1], 10);
    let previousVantage = null;
    let previousButton = null;
    let previousFloorNumber = null;
    let lastFoundFloor = null;

    for (let i = 0; i < settingsData.allCrmData.length; i++) {
      if (!settingsData.allCrmData[i].assets.vantage)
        continue;

      if (settingsData.allCrmData[i].modelName === lastClickedFlatCrmData.modelName &&
        settingsData.lastClickedFlat.userData.crm_data.buildingEngineId === settingsData.allCrmData[i].buildingEngineId) {
        let previousVantageIndex = null;
        const currentVantageIndex = settingsData.allCrmData[i].assets.vantage.split('media-index=')[1];

        const currentVantageYaw =
          parseInt(settingsData.allCrmData[i].assets.vantage.split('yaw=')[1], 10);
        const isCurrentVantageYawNaN = isNaN(currentVantageYaw) && typeof currentVantageYaw !== 'number';
        const isLastClickedFlatVantageYaw = isNaN(lastClickedFlatVantageYaw) && typeof lastClickedFlatVantageYaw !== 'number';
        if (lastClickedFlatVantageYaw !== currentVantageYaw && isCurrentVantageYawNaN !== isLastClickedFlatVantageYaw)
          continue;

        if (previousVantage)
          previousVantageIndex = previousVantage.split('media-index=')[1];

        if (previousVantageIndex === currentVantageIndex) {
          lastFoundFloor = previousFloorNumber = settingsData.allCrmData[i].floorNum;
          continue;
        }

        previousVantage = settingsData.allCrmData[i].assets.vantage;
        if (previousButton && previousFloorNumber && parseInt(previousButton.innerHTML) !== parseInt(previousFloorNumber))
          previousButton.innerHTML = `${previousButton.innerHTML}-${previousFloorNumber}`;

        if (previousButton && (parseInt(previousButton.innerHTML.split('-')[1]) === parseInt(previousFloorNumber)))
          previousFloorNumber = null;

        if (parseInt(previousButton?.innerHTML, 10) === parseInt(settingsData.allCrmData[i].floorNum, 10))
          continue;

        const button = createHtmlElement(
          this.floorButtons,
          settingsData.allCrmData[i].floorNum,
          'div',
          ['traverse-switcher__position']
        );
        button.setAttribute('vantage-url', settingsData.allCrmData[i].assets.vantage);

        previousButton = button;
      }
    }

    if(!previousButton)
      return;

    if (parseInt(previousButton.innerHTML.split('-')[0]) < parseInt(lastFoundFloor)) {
      if (lastFoundFloor)
        previousButton.innerHTML = `${previousButton.innerHTML}-${lastFoundFloor}`
    }
  }

  addControlButtons() {
    if (this.floorButtons.children.length <= 1)
      return;

    this.controlUp = createHtmlElement(this.element, '', 'button', ['traverse-switcher__control-button', 'traverse-switcher__control-button-up', 'traverse-switcher__button', 'icon-up2']);
    this.controlDown = createHtmlElement(this.element, '', 'button', ['traverse-switcher__control-button', 'traverse-switcher__control-button-down', 'traverse-switcher__button', 'icon-down2']);
  }
}